/* Custom customer reviews
   - Load More reveals cards in per-breakpoint chunks.
   - Helpful button: click toggles "voted" (heart + count+1) and
     persists in sessionStorage keyed by section + review id.
   - Write Review modal: opens on the CTA click; form submit shows
     a thank-you state (no backend — sits as social-proof polish). */
(function () {
  if (customElements.get('customer-reviews')) return;

  class CustomerReviews extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-cr-grid]');
      this.cards = Array.from(this.querySelectorAll('[data-cr-card]'));
      this.btn = this.querySelector('[data-cr-load]');
      this.countEl = this.querySelector('[data-cr-count]');
      this.template = this.dataset.countTemplate || '[shown] of [total] reviews';
      this.total = parseInt(this.dataset.total, 10) || this.cards.length;
      this.stepDesktop = parseInt(this.dataset.stepDesktop, 10) || 4;
      this.stepMobile = parseInt(this.dataset.stepMobile, 10) || 2;
      this.storageKey = this.dataset.storageKey || 'cr-default';

      const mq = window.matchMedia('(min-width: 990px)');
      const initial = mq.matches
        ? parseInt(this.dataset.initialDesktop, 10) || 10
        : parseInt(this.dataset.initialMobile, 10) || 6;

      this.visible = Math.min(initial, this.cards.length);
      this._render();

      this.btn?.addEventListener('click', () => {
        const step = mq.matches ? this.stepDesktop : this.stepMobile;
        this.visible = Math.min(this.visible + step, this.cards.length);
        this._render();
      });

      mq.addEventListener?.('change', () => {
        this.visible = Math.max(
          this.visible,
          mq.matches
            ? parseInt(this.dataset.initialDesktop, 10) || 10
            : parseInt(this.dataset.initialMobile, 10) || 6
        );
        this._render();
      });

      this._setupHelpful();
      if (this.dataset.modalEnabled === 'true') {
        this._setupModal();
      }
    }

    _render() {
      this.cards.forEach((c, i) => {
        c.classList.toggle('custom-cr__card--hidden', i >= this.visible);
      });
      if (this.btn) {
        this.btn.hidden = this.visible >= this.cards.length;
      }
      if (this.countEl) {
        this.countEl.textContent = this.template
          .replace('[shown]', String(this.visible))
          .replace('[total]', String(this.total));
      }
    }

    /* === Helpful vote (toggle) === */
    _setupHelpful() {
      const buttons = this.querySelectorAll('[data-cr-helpful]');
      buttons.forEach((btn) => {
        const id = btn.dataset.reviewId;
        const base = parseInt(btn.dataset.baseCount, 10) || 0;
        if (this._hasVoted(id)) {
          this._applyVoted(btn, base + 1);
        }
        btn.addEventListener('click', () => {
          if (this._hasVoted(id)) {
            this._unvote(id);
            this._applyUnvoted(btn, base);
          } else {
            this._markVoted(id);
            this._applyVoted(btn, base + 1);
          }
        });
      });
    }
    _storageId(id) { return `${this.storageKey}:helpful:${id}`; }
    _hasVoted(id) {
      try { return sessionStorage.getItem(this._storageId(id)) === '1'; }
      catch (e) { return false; }
    }
    _markVoted(id) {
      try { sessionStorage.setItem(this._storageId(id), '1'); } catch (e) {}
    }
    _unvote(id) {
      try { sessionStorage.removeItem(this._storageId(id)); } catch (e) {}
    }
    _applyVoted(btn, count) {
      btn.classList.add('is-voted');
      btn.setAttribute('aria-pressed', 'true');
      const emoji = btn.querySelector('.custom-cr__helpful-emoji');
      const countEl = btn.querySelector('.custom-cr__helpful-count');
      if (emoji) emoji.textContent = '❤️';
      if (countEl) countEl.textContent = String(count);
    }
    _applyUnvoted(btn, count) {
      btn.classList.remove('is-voted');
      btn.setAttribute('aria-pressed', 'false');
      const emoji = btn.querySelector('.custom-cr__helpful-emoji');
      const countEl = btn.querySelector('.custom-cr__helpful-count');
      if (emoji) emoji.textContent = '👍';
      if (countEl) countEl.textContent = String(count);
    }

    /* === Write Review modal === */
    _setupModal() {
      this.modal = this.querySelector('[data-cr-modal]');
      if (!this.modal) return;
      this.form = this.modal.querySelector('[data-cr-form]');
      this.thanks = this.modal.querySelector('[data-cr-thanks]');
      this.openers = this.querySelectorAll('[data-cr-modal-open]');
      this.closers = this.modal.querySelectorAll('[data-cr-modal-close]');

      this.openers.forEach((el) => el.addEventListener('click', () => this._openModal()));
      this.closers.forEach((el) => el.addEventListener('click', () => this._closeModal()));

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
          this._closeModal();
        }
      });

      // Rating picker: click sets rating, hover previews.
      const picker = this.modal.querySelector('[data-cr-rating]');
      const hidden = this.modal.querySelector('[data-cr-rating-input]');
      if (picker && hidden) {
        const stars = Array.from(picker.querySelectorAll('.custom-cr__rating-star'));
        const paint = (n, klass) => {
          stars.forEach((s, i) => s.classList.toggle(klass, i < n));
        };
        stars.forEach((s) => {
          s.addEventListener('mouseenter', () => {
            picker.dataset.hover = '1';
            paint(parseInt(s.dataset.star, 10), 'is-hover');
          });
          s.addEventListener('mouseleave', () => {
            delete picker.dataset.hover;
            paint(0, 'is-hover');
          });
          s.addEventListener('click', () => {
            const n = parseInt(s.dataset.star, 10);
            hidden.value = String(n);
            paint(n, 'is-on');
            stars.forEach((el, i) => el.setAttribute('aria-checked', i < n ? 'true' : 'false'));
          });
        });
      }

      // File field: reflect chosen file name.
      const file = this.modal.querySelector('[data-cr-file]');
      const fileName = this.modal.querySelector('[data-cr-file-name]');
      if (file && fileName) {
        file.addEventListener('change', () => {
          fileName.textContent = file.files?.[0]?.name || '';
        });
      }

      // Submit: fake success. Rating must be > 0.
      this.form?.addEventListener('submit', (e) => {
        e.preventDefault();
        const rating = parseInt(hidden?.value || '0', 10);
        if (!this.form.checkValidity() || rating < 1) {
          this.form.reportValidity();
          if (rating < 1) picker?.animate?.([{ transform: 'translateX(-4px)' }, { transform: 'translateX(4px)' }, { transform: 'translateX(0)' }], { duration: 250 });
          return;
        }
        const submit = this.form.querySelector('.custom-cr__submit');
        if (submit) { submit.disabled = true; submit.textContent = 'Submitting…'; }
        // Brief fake latency for realism, then swap to thanks state.
        setTimeout(() => {
          this.form.hidden = true;
          if (this.thanks) this.thanks.hidden = false;
        }, 500);
      });
    }

    _openModal() {
      if (!this.modal) return;
      // Portal the modal into <body> once so ancestor containment
      // (content-visibility, transform, filter, contain) can never trap
      // its position:fixed positioning. Without this, the modal opens
      // then vanishes as the section's layout re-evaluates.
      if (this.modal.parentElement !== document.body) {
        // The section-scoped CSS vars (panel bg, accent, backdrop, etc.)
        // won't inherit once the modal is a child of <body>. Copy them
        // as inline styles so the modal keeps the customizer colors.
        const styles = getComputedStyle(this);
        [
          '--cr-modal-panel-bg',
          '--cr-modal-backdrop',
          '--cr-modal-text',
          '--cr-modal-input-border',
          '--cr-modal-accent',
        ].forEach((v) => {
          const val = styles.getPropertyValue(v).trim();
          if (val) this.modal.style.setProperty(v, val);
        });
        document.body.appendChild(this.modal);
      }
      this.modal.classList.add('is-open');
      this.modal.hidden = false;
      document.documentElement.classList.add('custom-cr-modal-open');
      // Focus first field for accessibility.
      const first = this.modal.querySelector('input, textarea, button');
      first?.focus({ preventScroll: true });
    }
    _closeModal() {
      if (!this.modal) return;
      this.modal.classList.remove('is-open');
      this.modal.hidden = true;
      document.documentElement.classList.remove('custom-cr-modal-open');
      // Reset back to a fresh form view so a second open shows a blank form.
      if (this.form) {
        this.form.hidden = false;
        this.form.reset();
        // Clear rating picker visual + hidden value.
        const picker = this.form.querySelector('[data-cr-rating]');
        const hidden = this.form.querySelector('[data-cr-rating-input]');
        if (hidden) hidden.value = '0';
        if (picker) {
          picker.querySelectorAll('.custom-cr__rating-star').forEach((s) => {
            s.classList.remove('is-on', 'is-hover');
            s.setAttribute('aria-checked', 'false');
          });
        }
        const fileName = this.form.querySelector('[data-cr-file-name]');
        if (fileName) fileName.textContent = '';
      }
      if (this.thanks) this.thanks.hidden = true;
      const submit = this.form?.querySelector('.custom-cr__submit');
      if (submit) { submit.disabled = false; submit.textContent = submit.dataset.origText || submit.textContent; }
    }
  }

  customElements.define('customer-reviews', CustomerReviews);
})();
